<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <b-card-title>
                    {{ $t('org.billing.Daily Cost') }}
                </b-card-title>
            </div>
            <b-card-actions-container
                v-if="!editMode && data.length && !viewTypeIsMobile"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>
        <template v-if="data.length">
            <div class="position-relative" :style="{ height: 'calc(100% - 78px)'}">
                <DailyLineChart
                    ref="container"
                    :svg="svg"
                    :data="data"
                    :status="status"
                    :month="selectedMonth"
                    :dataWithCost="dataWithCost"
                    @resize:svg="handleResize"
                    @detail-layer="modalShowDetail"
                >
                </DailyLineChart>
                <div
                    ref="REF_COLLAPSE"
                    :style="collapseResponsiveStyle"
                >
                    <DailyLineTable
                        v-show="!editMode && isVisible"
                        :data="data"
                        :dataWithCost="dataWithCost"
                        @detail-layer="modalShowDetail"
                    ></DailyLineTable>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="d-flex justify-content-center align-items-center flex-column text-center">
                <SVGEmptyIcon/>
                <span>{{ $t('org.dashboard.No data') }}</span>
            </div>
        </template>
    </b-overlay>
</template>

<script>
import BCardActionsContainer from '@/components/BCardActionsContainer'
import DailyLineChart from './DailyLineChart'
import DailyLineTable from './DailyLineTable'
import useSelectMonth from '@/composables/useSelectMonth'
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import useDashboard from '@/composables/useDashboard'
import { ref, toRefs, computed, getCurrentInstance, watch } from '@vue/composition-api'
import SVGEmptyIcon from '@/assets/images/empty.svg'
import useDashboardRender from '@/composables/useDashboardRender'
export default {
    components: {
        BCardActionsContainer,
        DailyLineChart,
        DailyLineTable,
        SVGEmptyIcon
    },
    props: {
        editMode: {
            type: Boolean,
            default: false
        },
        isOpen: {
            type: Boolean,
            default: false
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const svg = ref({
            width: 0,
            height: 0,
            paddingL: 55,
            paddingR: 55,
            paddingT: 10,
            paddingB: 28
        })
        const { isOpen } = toRefs(props)
        const { viewType } = useDashboardRender()
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }
        const isVisible = ref(false)
        const tableHeight = ref(null)
        const REF_COLLAPSE = ref(null)
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'summary-card-dates', status })
        }
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            vm.$nextTick(() => {
                tableHeight.value = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                handleResize()
            })
        })
        const { selectedMonth } = useSelectMonth()
        const { selectedProject } = useSelectProjectAndGroup()
        const { getSummaryDates } = useDashboard()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, datesCostData] = getSummaryDates(currentParams.value)
        const data = computed(() => datesCostData.value.dates)
        const container = ref(null)
        const dataWithCost = computed(() => {
            return data.value.filter(item => item.cost !== 0).map(item => {
                return {
                    cost: item.cost,
                    date: item.date,
                    products: item.products.sort((a, b) => Math.abs(a.cost) < Math.abs(b.cost) ? 1 : -1)
                }
            })
        })
        const handleResize = () => {
            if (container.value) {
                svg.value.width = container.value.$el.clientWidth
                svg.value.height = container.value.$el.clientHeight - (svg.value.paddingB * 1.7) - tableHeight.value
            }
        }
        const modalShowDetail = (params) => {
            context.emit('modal:showDetail', params)
        }
        watch(currentParams, (value, oldValue) => {
            getSummaryDates(value)
        }, {
            deep: true
        })
        return {
            REF_COLLAPSE,
            data,
            status,
            svg,
            container,
            dataWithCost,
            handleResize,
            modalShowDetail,
            selectedMonth,
            selectedProject,
            isVisible,
            triggerCollapse,
            collapseResponsiveStyle,
            viewTypeIsMobile
        }
    }
}
</script>

<style>
#containerDailyCost text.shadow {
    text-shadow: #fff 2px 2px 10px
}
#containerDailyCost .list-enter-active, #containerDailyCost .list-leave-active {
    transition: all 1s;
}
#containerDailyCost .list-enter  {
    opacity: 0;
    transform: translateY(30px);
}

#containerDailyCost .list-leave-to  {
    opacity: 0;
    transform: translateX(30px);
}
</style>
